import { T, Var } from 'gt-react';
import type {
  CustomerAliasKind,
  MemoryProposalPayload,
} from '@erp/shared-types';

function addressLine(address: {
  street: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
}): string {
  return [address.street, address.city, address.postalCode, address.country]
    .filter(Boolean)
    .join(', ');
}

/**
 * Human-readable statement of what a rule (or proposal payload) does. One
 * sentence per kind; ids are shown through `labels` when the backend resolved
 * them. Unknown kinds fall back to the raw JSON so nothing is hidden.
 */
export function RuleValueRenderer({
  payload,
  labels,
}: {
  payload: MemoryProposalPayload;
  labels: Record<string, string>;
}) {
  const name = (id: string) => labels[id] ?? id;
  switch (payload.kind) {
    case 'customer_alias':
      return (
        <CustomerAliasValue
          aliasKind={payload.aliasKind}
          alias={payload.alias}
          customerName={name(payload.customerId)}
        />
      );
    case 'location_is_own_site':
      return (
        <T context="memory rule: an address is one of our own sites">
          <Var>{addressLine(payload.address)}</Var> is our own site (
          <Var>{payload.label}</Var>); never infer the customer from it.
        </T>
      );
    case 'ship_to_alias':
      return (
        <T context="memory rule: an address maps to a stored location">
          Ship-to <Var>{addressLine(payload.address)}</Var> is the location{' '}
          <strong>
            <Var>{name(payload.locationId)}</Var>
          </strong>
          <Var>
            {payload.customerId ? (
              <>
                {' '}
                of <Var>{name(payload.customerId)}</Var>
              </>
            ) : null}
          </Var>
          .
        </T>
      );
    case 'carrier_term_alias':
      return (
        <T context="memory rule: document phrasing maps to a shipping method code">
          “<Var>{payload.term}</Var>” means shipping method{' '}
          <strong>
            <Var>{payload.shippingMethodCode}</Var>
          </strong>
          <Var>
            {payload.customerId ? (
              <>
                {' '}
                for <Var>{name(payload.customerId)}</Var>
              </>
            ) : null}
          </Var>
          .
        </T>
      );
    case 'carrier_account_rule':
      return (
        <T context="memory rule: which carrier account a customer uses">
          <Var>{name(payload.customerId)}</Var> ships with{' '}
          <Var>{payload.carrierName}</Var> account{' '}
          <strong>
            <Var>{payload.accountNumber}</Var>
          </strong>
          <Var>
            {payload.condition.shippingMethodCode ||
            payload.condition.appliesTo ||
            payload.condition.incoterm ? (
              <>
                {' '}
                when{' '}
                <Var>
                  {[
                    payload.condition.shippingMethodCode,
                    payload.condition.appliesTo,
                    payload.condition.incoterm,
                  ]
                    .filter(Boolean)
                    .join(' / ')}
                </Var>
              </>
            ) : null}
          </Var>
          .
        </T>
      );
    case 'extraction_hint':
      return (
        <T context="memory rule: a hint appended to an extraction field">
          Extraction hint on <Var>{payload.fieldPath}</Var>: “
          <Var>{payload.hint}</Var>”
          <Var>
            {payload.senderDomain ? (
              <>
                {' '}
                for <Var>{payload.senderDomain}</Var>
              </>
            ) : null}
          </Var>
          <Var>
            {payload.customerIds?.length ? (
              <>
                {' '}
                (only for{' '}
                <strong>
                  <Var>{payload.customerIds.map(name).join(', ')}</Var>
                </strong>
                )
              </>
            ) : null}
          </Var>
          .
        </T>
      );
    case 'sender_route':
      return (
        <SenderRouteValue
          sender={payload.sender}
          byDomain={payload.senderKind === 'email_domain'}
          organizationName={
            payload.targetOrganizationId
              ? name(payload.targetOrganizationId)
              : null
          }
          note={payload.note}
          documentTypes={payload.documentTypes}
        />
      );
    case 'note':
      return <span className="whitespace-pre-wrap">{payload.note}</span>;
    case 'skill_draft':
      return (
        <T context="proposal payload: a drafted runtime skill">
          Skill “<Var>{payload.title}</Var>” — use when:{' '}
          <Var>{payload.whenToUse}</Var>
        </T>
      );
    default:
      return (
        <pre className="whitespace-pre-wrap text-xs">
          {JSON.stringify(payload, null, 2)}
        </pre>
      );
  }
}

function CustomerAliasValue({
  aliasKind,
  alias,
  customerName,
}: {
  aliasKind: CustomerAliasKind;
  alias: string;
  customerName: string;
}) {
  switch (aliasKind) {
    case 'name':
      return (
        <T context="memory rule: an extracted company name means a customer">
          When the document names “<Var>{alias}</Var>”, the customer is{' '}
          <strong>
            <Var>{customerName}</Var>
          </strong>
          .
        </T>
      );
    case 'email_domain':
      return (
        <T context="memory rule: a sender email domain means a customer">
          Orders from <Var>{alias}</Var> belong to{' '}
          <strong>
            <Var>{customerName}</Var>
          </strong>
          .
        </T>
      );
    case 'email_address':
      return (
        <T context="memory rule: a sender email address means a customer">
          Remittances sent from <Var>{alias}</Var> belong to{' '}
          <strong>
            <Var>{customerName}</Var>
          </strong>
          .
        </T>
      );
    case 'bank_payer':
      return (
        <T context="memory rule: a bank payer string on a cash receipt means a customer">
          Cash receipts whose bank payer reads “<Var>{alias}</Var>” belong to{' '}
          <strong>
            <Var>{customerName}</Var>
          </strong>
          .
        </T>
      );
  }
}

function SenderRouteValue({
  sender,
  byDomain,
  organizationName,
  note,
  documentTypes,
}: {
  sender: string;
  byDomain: boolean;
  organizationName: string | null;
  note: string | null;
  documentTypes: string[] | null;
}) {
  const scope = documentTypes?.length ? documentTypes.join(', ') : null;
  return (
    <span>
      {organizationName ? (
        byDomain ? (
          <T context="memory rule: documents from a sender domain route to an organization">
            Documents from anyone at <Var>{sender}</Var> belong to{' '}
            <strong>
              <Var>{organizationName}</Var>
            </strong>
            .
          </T>
        ) : (
          <T context="memory rule: documents from a sender address route to an organization">
            Documents from <Var>{sender}</Var> belong to{' '}
            <strong>
              <Var>{organizationName}</Var>
            </strong>
            .
          </T>
        )
      ) : (
        <T context="memory rule: a note the organization classifier reads about a sender">
          Note for the classifier about <Var>{sender}</Var>.
        </T>
      )}
      {scope ? (
        <>
          {' '}
          <T context="memory rule: which document types a sender route applies to">
            Applies to: <Var>{scope}</Var>.
          </T>
        </>
      ) : null}
      {note ? (
        <span className="block whitespace-pre-wrap text-muted-foreground">
          “{note}”
        </span>
      ) : null}
    </span>
  );
}
